import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { maiorQueZeroValidator } from '../../../../../../shared/validators/maior-que-zero.validator';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyConfig, NgxCurrencyDirective } from 'ngx-currency';

import { AlertCardComponent } from '../../../../../../shared/components/alert-card/alert-card.component';
import { AbntPipe } from '../../../../../../shared/pipes/abnt.pipe';
import { ProdutoListStruct } from '../../../../../../shared/services/structs/produto/produto-list.struct';
import { ContextoDePreco } from '../venda-item.form';
import { PlanoDoKit, montarPlanoDoKit, totalDoPlano, totalDoPlanoPorKit } from '../venda-item.kit';

export interface VdKitDialogData {
    produto: ProdutoListStruct;
    contexto: ContextoDePreco;
    exibeDescontoPorItem: boolean;
    tetoDesconto: number | null;
    mascaraPreco: Partial<NgxCurrencyConfig>;
    mascaraQuantidade: Partial<NgxCurrencyConfig>;
    mascaraInteira: Partial<NgxCurrencyConfig>;
    mascaraPercentual: Partial<NgxCurrencyConfig>;
}

/**
 * "Incluir kit" (§6.9): o que o usuário decide ANTES de o kit virar linhas.
 *
 * Quantidade de kits, preço do kit (só no preço fixo) e
 * desconto do kit — que passa pelo MESMO teto de desconto dos itens, porque vira o desconto de
 * cada componente. Os componentes aparecem em leitura com o preço resultante de cada um, para
 * o usuário ver o rateio antes de confirmar.
 *
 * O diálogo devolve o PLANO; quem cria as linhas é a seção de itens.
 */
@Component({
    selector: 'app-vd-kit-dialog',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        NgxCurrencyDirective,
        AlertCardComponent,
        AbntPipe,
    ],
    templateUrl: './vd-kit-dialog.component.html',
    styleUrl: './vd-kit-dialog.component.css',
})
export class VdKitDialogComponent {
    public readonly form: FormGroup;
    public plano: PlanoDoKit;

    constructor(
        private fb: FormBuilder,
        private dialogRef: MatDialogRef<VdKitDialogComponent, PlanoDoKit | undefined>,
        @Inject(MAT_DIALOG_DATA) public data: VdKitDialogData,
    ) {
        this.plano = montarPlanoDoKit(data.produto, data.contexto, 1, null, 0);

        this.form = this.fb.group({
            quantidadeKits: [1, [Validators.required, maiorQueZeroValidator()]],
            precoKit: [this.plano.precoKit, Validators.min(0)],
            percentualDescontoKit: [0, [Validators.min(0), Validators.max(100)]],
        });

        // Kit por soma: o preço é a soma dos componentes e não se digita. Desabilitado, e não
        // readonly, para o campo mostrar isso.
        if (!this.podeEditarPreco) this.form.get('precoKit')?.disable({ emitEvent: false });

        // Preço e desconto são dois nomes para a mesma negociação: cada um recalcula o outro,
        // sem laço (o plano escreve com emitEvent false).
        this.form.get('quantidadeKits')?.valueChanges.subscribe(() => this.replanejar(null));
        this.form.get('percentualDescontoKit')?.valueChanges.subscribe(() => this.replanejar(null));
        this.form.get('precoKit')?.valueChanges.subscribe((preco) => {
            if (this.podeEditarPreco) this.replanejar(Number(preco ?? 0));
        });
    }

    public get isPrecoFixo(): boolean {
        return !!this.data.produto.isPrecoFixoKit;
    }

    /** Preço do kit só se edita no preço FIXO; no kit por soma ele é derivado dos componentes. */
    public get podeEditarPreco(): boolean {
        return this.isPrecoFixo;
    }

    public get totalPorKit(): number {
        return totalDoPlanoPorKit(this.plano);
    }

    public get totalGeral(): number {
        return totalDoPlano(this.plano);
    }

    public get semPreco(): boolean {
        return this.plano.precoKitLista <= 0;
    }

    public get excedeuOTeto(): boolean {
        return this.data.tetoDesconto != null && this.plano.percentualDescontoKit > this.data.tetoDesconto;
    }

    public get semComponentes(): boolean {
        return !(this.data.produto.composicaoKit?.length);
    }

    private replanejar(precoInformado: number | null): void {
        const kits = Number(this.form.get('quantidadeKits')?.value ?? 0);
        const desconto = Number(this.form.get('percentualDescontoKit')?.value ?? 0);

        this.plano = montarPlanoDoKit(this.data.produto, this.data.contexto, kits, precoInformado, desconto);

        // O outro lado da negociação acompanha, sem reentrar.
        this.form.patchValue(
            { precoKit: this.plano.precoKit, percentualDescontoKit: this.plano.percentualDescontoKit },
            { emitEvent: false },
        );
    }

    public confirmar(): void {
        if (this.form.invalid || this.semComponentes || this.plano.quantidadeKits <= 0 || !this.plano.fechouNoPreco) {
            this.form.markAllAsTouched();
            return;
        }

        this.dialogRef.close(this.plano);
    }

    public cancelar(): void {
        this.dialogRef.close(undefined);
    }
}
